import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "../AuthProvider";
import {
  EventSourceOrNull,
  EventType,
  SSEContextType,
  SSEProviderProps,
  UseSSEContextInterface,
} from "./types";
import { useEnabledFeatureFlag } from "../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../common/feature-flagging/FeatureFlags";
import fetchAuthToken from "../../utils/fetchAuthToken";
import UserAccessControlUtils, {
  FEATURE_IRP5S,
  FEATURE_PAYSLIPS,
  FEATURE_ROSTERS,
  FEATURE_TIMESHEETS,
} from "../../utils/UserAccessControlUtils";

const SSEContext = createContext<SSEContextType | null>(null);
const SSEProvider: React.FC<SSEProviderProps> = ({
  children,
  onErrorMessage,
}) => {
  const canConnectToSSE = useEnabledFeatureFlag(FeatureFlags.SSE_CONNECT);

  const [isConnected, setIsConnected] = useState(false);

  const auth = useContext(AuthContext);

  const userHasRelevantFeaturesForSSEConnection =
    UserAccessControlUtils.hasFeature(auth?.user_features || [], [
      FEATURE_PAYSLIPS,
      FEATURE_TIMESHEETS,
      FEATURE_ROSTERS,
      FEATURE_IRP5S,
    ]);

  const maxAttempts: number = parseInt(
    process.env.REACT_APP_SSE_MAX_ATTEMPTS ?? "5",
    10
  );
  const [eventSource, setEventSource] = useState<EventSourceOrNull>(null);
  const [attempt, setAttempt] = useState(0);

  const connect = useCallback(
    async (timestamp: number | null) => {
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }

      if (!userHasRelevantFeaturesForSSEConnection) {
        return;
      }

      if (attempt > maxAttempts) {
        onErrorMessage?.(
          `Failed to connect to SSE after ${maxAttempts} attempts`,
          "error"
        );
        return;
      }

      await fetchAuthToken({ expire_current_token: true });

      const backoffDelay =
        attempt === 0 ? 0 : Math.min(2 ** attempt * 1000, 30000);

      let url = `${
        process.env.REACT_APP_TASK_SERVICE_URL as string
      }/sse/subscribe/${auth?.employer.id}`;
      if (timestamp) {
        url += `/${timestamp}`;
      } else {
        url += `/${Date.now() - 15 * 60 * 1000}`;
      }
      const timeoutId = setTimeout(() => {
        const es = new EventSource(url, {
          withCredentials: true,
        } as EventSourceInit);

        es.onerror = () => {
          onErrorMessage?.(
            `Failed to connect to SSE, retrying in ${
              backoffDelay / 1000
            } seconds`,
            "warning"
          );
          es.close();
          setAttempt((prevAttempt) => prevAttempt + 1);
          connect(Date.now());
        };

        es.onopen = () => {
          setAttempt(0);
          setIsConnected(true);
        };

        setEventSource(es);
      }, backoffDelay);

      // eslint-disable-next-line consistent-return
      return () => {
        clearTimeout(timeoutId);
      };
    },
    [eventSource, maxAttempts, onErrorMessage, auth?.employer.id, attempt]
  );

  useEffect(() => {
    if (!canConnectToSSE) {
      onErrorMessage?.("SSE is disabled", "warning");
      return;
    }

    if (isConnected) {
      return; // already connected
    }

    const timestamp = sessionStorage.getItem("lastSSETimestamp")
      ? parseInt(sessionStorage.getItem("lastSSETimestamp") as string, 10)
      : null;
    connect(timestamp);

    // eslint-disable-next-line consistent-return
    return () => {
      eventSource?.close();
    };
  }, [isConnected, canConnectToSSE]);

  const listen = useCallback(
    (eventType: EventType, callback: (event: MessageEvent) => void) => {
      if (!eventSource) return () => null;

      const eventListener = (event: MessageEvent): void => {
        sessionStorage.setItem("lastSSETimestamp", Date.now().toString());
        callback(event);
      };

      eventSource.addEventListener(eventType, eventListener);
      return () => eventSource.removeEventListener(eventType, eventListener);
    },
    [eventSource]
  );

  const value = useMemo(
    () => ({
      listen: eventSource ? listen : undefined,
    }),
    [listen, eventSource]
  );

  if (!auth) {
    onErrorMessage?.("Client is not authenticated", "warning");
    return <SSEContext.Provider value={null}>{children}</SSEContext.Provider>;
  }

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};

const useSSEvents = (
  eventType: EventType,
  callback: (event: MessageEvent) => void
): UseSSEContextInterface => {
  const context = useContext(SSEContext);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!context) {
    throw new Error("useSSE must be used within a SSEProvider");
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (context.listen) {
      setLoading(true);
      setError(null);
      try {
        const unsubscribe = context.listen(eventType, callback);
        setConnected(true);
        return () => unsubscribe();
      } catch (e) {
        setError(`Failed to listen to events`);
        setLoading(false);
      }
    }
  }, [context.listen, eventType, callback]);

  return { connected, loading, error };
};

export { SSEProvider, useSSEvents };
